import { useApplicationRegisters } from '/@/hooks/application-register';
import { useCropMonitoringNotes } from '/@/hooks/crop-monitoring-notes';
import { useFieldById } from '/@/hooks/fields';
import { useReports } from '/@/hooks/reports';
import { useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { useVisitReports } from '/@/hooks/visit-reports';
import { useGetRecommendationsFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { type CampaignWindow, useCampaignWindow } from './use-campaign-window';

export interface CampaignRegistries {
    window: CampaignWindow;
    fetchEnabled: boolean;
    dateFetchEnabled: boolean;
    notesQuery: ReturnType<typeof useCropMonitoringNotes>;
    applicationsQuery: ReturnType<typeof useApplicationRegisters>;
    applicationReportsQuery: ReturnType<typeof useReports>;
    seedRecommendationsQuery: ReturnType<typeof useGetRecommendationsFromAPI>;
    cropProtectionRecommendationsQuery: ReturnType<typeof useGetRecommendationsFromAPI>;
    visitReportsQuery: ReturnType<typeof useVisitReports>;
}

/**
 * Campaign-scoped registry queries shared by the timeline and stage banners. Every caller passes
 * the same field + crop-monitoring keys, so SWR dedupes the underlying requests.
 */
export const useCampaignRegistries = ({
    fieldId,
    cropMonitoring,
    enabled = true,
}: {
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    enabled?: boolean;
}): CampaignRegistries => {
    const window = useCampaignWindow({ field: fieldId, cropMonitoring });
    const fieldQuery = useFieldById({ id: fieldId ?? 0 }, { enabled: !!fieldId });
    const fetchEnabled = enabled && Boolean(fieldId);
    const dateFetchEnabled = fetchEnabled && window.windowReady;

    // Deliberately NOT date-bounded, unlike every other query here. The others filter on the
    // date of the event they describe; a note is filtered on when it was written, and a note
    // about a finished campaign is naturally written after it ends. Bounding by the campaign
    // window hid every note added to a harvested crop monitoring — it saved, then vanished on
    // the next revalidation. Notes attached to a register are scoped by their foreign key, so
    // they need no date filter at all; standalone notes are windowed by the caller instead
    // (see the noteEntries filter in registries/index.tsx). Notes are a handful of
    // user-written observations per field, so fetching the field's full set is cheap.
    const notesQuery = useCropMonitoringNotes({
        field: fieldId,
        ordering: 'created',
        pageSize: 10000,
    });
    const applicationsQuery = useApplicationRegisters({
        field: fieldId,
        applicationDateGte: window.dateGte,
        applicationDateLte: window.dateLte,
        ordering: 'application_date',
        pageSize: 10000,
    });
    const applicationReportsQuery = useReports({ fieldId, type: 'application' }, { enabled: fetchEnabled });
    const seedRecommendationsQuery = useGetRecommendationsFromAPI(
        {
            page: 1,
            pageSize: 10000,
            filters: {
                type__in: 'corn,soy,corn_density',
                planting_date_gte: window.dateGte,
                planting_date_lte: window.dateLte,
            },
            ordering: 'created',
            fieldId,
        },
        undefined,
        dateFetchEnabled
    );
    const cropProtectionRecommendationsQuery = useGetRecommendationsFromAPI(
        {
            page: 1,
            pageSize: 10000,
            filters: {
                type__in: 'weed,disease,plague,seed_treatment',
                created_gte: window.cpDateGte,
                created_lte: window.cpDateLte,
            },
            ordering: 'created',
            fieldId,
        },
        undefined,
        dateFetchEnabled
    );

    const isRv = useIsValidatedRV();
    const isConsultant = useIsValidatedConsultant();
    const visitReportsQuery = useVisitReports(
        {
            fieldId,
            campaign: cropMonitoring?.campaign ? Number(cropMonitoring.campaign) : fieldQuery.data?.campaign,
            status: !(isRv || isConsultant) ? 'saved' : undefined,
            ordering: 'date',
        },
        { enabled: fetchEnabled }
    );

    return {
        window,
        fetchEnabled,
        dateFetchEnabled,
        notesQuery,
        applicationsQuery,
        applicationReportsQuery,
        seedRecommendationsQuery,
        cropProtectionRecommendationsQuery,
        visitReportsQuery,
    };
};
